import { useEffect } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import jwtDecode from "jwt-decode";

import "./App.scss";

import { ACCOUNT_TYPES } from "./data/constants";

import Login from "./pages/Login/index";
import NotFound from "./pages/NotFound/index";
import Home from "./pages/Home/index";
import RequireAuth from "./components/RequireAuth/index";

import useAuth from "./hooks/useAuth";
import useUser from "./hooks/useUser";
import Sidebar from "./components/Sidebar";
import SingleViewOutgoingNominations from "./pages/Nominations/Outgoing/SingleView";
import SingleViewIncomingNominations from "./pages/Nominations/Incoming/SingleView";
import LearningAgreementList from "./pages/LearningAgreements/Listing";
import withNavBar from "./components/WithNavBar";
import withFooter from "./components/WithFooter";
import Register from "./pages/Register";
import RegisterConfirmation from "./pages/RegisterConfirmation";
import ReusableComponents from "./pages/ReusableComponents";
import PendingRequests from "./pages/PendingRequests";
import ExportPdf from "./pages/Nominations/ExportPdf";
import NominationsList from "./pages/Nominations/Listing";
import SingleViewLA from "./pages/LearningAgreements/View";
import ExportOlaPdf from "./pages/LearningAgreements/View/components/ExportOlaPdf";
import RequirementsNominations from "./pages/Nominations/Requirements";
import AccountsAndAccess from "./pages/AccountsAndAccess";
import OrganizationalUnits from "./pages/OrganizationalUnits";
import Upload from "./pages/LearningAgreements/Upload";
import ShortTermMobilitiesList from "./pages/ShortTermMobilties/Listing";
import SingleViewShortTermMobility from "./pages/ShortTermMobilties/View";
import ExportShortTermPdf from "./pages/ShortTermMobilties/View/components/ExportShortTermPdf";
import withTitle from "./components/withTitle";
import GET_EWP_SETTINGS from "./graphql/queries/settings/getEWPSettings";
import { useQuery, NetworkStatus } from "@apollo/client";
import SingleViewIIAs from "./pages/IIAs/SingleView";
import ViewPdf from "./pages/IIAs/ViewPdf";
import InterInstitutionalAgreementList from "./pages/IIAs/Listing";
import CreateIIA from "./pages/IIAs/Create";
import EditFactsheet from "./pages/IIAs/EditFactsheet";
import EwpSettings from "./pages/EwpSettings";
import ImportIIA from "./pages/IIAs/Import";
import UsefulInfo from "./pages/ErasmusPlusApp/UsefulInfo";
import Event from "./pages/ErasmusPlusApp/Events";
import Deals from "./pages/ErasmusPlusApp/Deals";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TermsAndConditions from "./pages/TermsAndConditions";
import ExportIIAs from "./pages/IIAs/Export";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import UkraineAssistanceSettings from "./pages/UkraineAssistance";
import ArticleSingleView from "./pages/ArticleSingleView";
import CoursesEvaluationsList from "./pages/CoursesEvaluations/Listing";
import CoursesEvaluationsSingleView from "./pages/CoursesEvaluations/SingleView";
import ExportPdfCourse from "./pages/CoursesEvaluations/ExportPdf";
import { useTranslation, Trans } from 'react-i18next';
import ImportNomination from "./pages/Nominations/Import";
import AllArticles from "./pages/AllArticles";
import { VerifyGuest } from "./pages/VerifyGuest";
import OutgoingTrainseeshipList from './pages/Traineeships/Listing'
import SingleViewTraineeship from "./pages/Traineeships/View";
import FactsheetBrowser from "./pages/FactsheetBrowser/Listing"
import PartnerFactSheetSingleView from "./pages/FactsheetBrowser/View";

import CourseCatalogue from "./pages/CourseCatalogue";
import Programmes from "./pages/CourseCatalogue/Components/Programme";
import SingleViewProgramme from "./pages/CourseCatalogue/Components/Programme/View";
import Courses from "./pages/CourseCatalogue/Components/Course";
import SingleViewCourse from "./pages/CourseCatalogue/Components/Course/View";
import UploadProgramme from "./pages/CourseCatalogue/Components/Programme/Upload";
import UploadCourse from "./pages/CourseCatalogue/Components/Course/Upload";
import Landing from "./pages/Landing";
import ExportTraineeshipPdf from "./pages/Traineeships/View/components/ExportTraineeshipPdf";
import CertificatesArrivalDeparturesList from "./pages/CertificatesArrivalDepartures/Listing";
import CertificatesOutgoingView from "./pages/CertificatesArrivalDepartures/OutgoingView";

function App() {
  const { auth, setAuth } = useAuth();
  const { setUser, user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      const decodedToken = jwtDecode(token);

      let currentUser = {
        type:
          decodedToken.userType === "HEI"
            ? ACCOUNT_TYPES.hei
            : ACCOUNT_TYPES.eufAdmin,
        heiID: decodedToken.heiID,
        email: decodedToken.email,
      };

      if (currentUser.type === ACCOUNT_TYPES.hei) {
        currentUser = {
          ...currentUser,
          userId: decodedToken.userId,
          erasmusCode: decodedToken.erasmusCode,
          userName: decodedToken.userName,
          permissions: decodedToken.permissions,
        };
      }

      setUser(currentUser);
      setAuth({ accessToken: token });
      navigate(location.pathname, { replace: true });

      if (location.pathname === "/") {
        navigate("/home", { replace: true })
      }
    } else {
      setAuth({});
      setUser({});
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: 0, left: 0, behavior: "instant"});
    }, 0);
  }, [location.pathname]);

  const {
    loading: loadingEWPSettings,
    error: errorEWPSettings,
    data: dataEWPSettings,
    refetch: refetchQuery,
    networkStatus: networkStatusEWPSettings,
  } = useQuery(GET_EWP_SETTINGS, {
    notifyOnNetworkStatusChange: true,
    skip: !user?.email,
  });

  useEffect(() => {
    refetchQuery();
  }, [navigate]);

  return (
    <Routes>
      <Route path="login" element={<Login />} />
      <Route path="verify-guest" element={<VerifyGuest />} />
      <Route path="privacy-policy-terms-conditions" element={<TermsAndConditions />} />
      <Route path="register">
        <Route index element={<Register />} />
        <Route
          path="confirm/:registrationId"
          element={<RegisterConfirmation />}
        />
      </Route>
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Landing/>} />
      
      {/* Traineeship PDF Export - Accessible without auth (for guests) */}
      <Route
        path="/traineeships/outgoing/export/:traineeshipID"
        element={<ExportTraineeshipPdf />}
      />
      
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.eufAdmin, ACCOUNT_TYPES.hei]}
          />
        }
      >
        <Route path="/home" element={Sidebar(
          withFooter(
            withNavBar(
              withTitle((props) => <Home {...props} />, t('home.tab')),
              t('home.title'),
              ""
            )
          )
          )} />
      </Route>
      <Route
        element={<RequireAuth allowedUserTypes={[ACCOUNT_TYPES.eufAdmin]} />}
      >
        <Route path="/requests" element={Sidebar(PendingRequests)} />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["creatingAccounts"]}
          />
        }
      >
        <Route
          path="/accounts-and-access"
          element={Sidebar(AccountsAndAccess)}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
          />
        }
      >
        <Route
          path="/news"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <AllArticles {...props} />, t('allArticles.title')),
                t('allArticles.title'),
                t('allArticles.subTitle')
              )
            )
          )}
        />
        <Route
          path="/news/single-view/:id"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <ArticleSingleView {...props} />, ""),
                t('articleSingleView.title'),
                t('articleSingleView.subTitle')
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["managingOUnits"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_ounits),
            ]}
          />
        }
      >
        <Route
          path="/organizational-units"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <OrganizationalUnits {...props} userHeiID={user.heiID} />
                  ),
                  ""
                ),
                t('organizationalUnits.title'),
                ""
              )
            )
          )}
        />        
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
          />
        }
      >
        <Route
          path="/ukraine-assistance"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <UkraineAssistanceSettings {...props} />, ""),
                t('ukraineAssistance.title'),
                ""
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["ewpSettings"]}
          />
        }
      >
        <Route
          path="/ewp/settings"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <EwpSettings {...props} />, ""),
                t('ewpSettings.title'),
                ""
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["viewingStudents", "managingStudents"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_ola),
            ]}
          />
        }
      >
        <Route
          path="/ola/outgoing"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <LearningAgreementList
                      {...props}
                      typeOfStudent={1}
                      userHeiID={user.heiID}
                    />
                  ),
                  t('outgoingStudents.tab')
                ),
                t('outgoingStudents.title'),
                t('outgoingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/ola/incoming"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <LearningAgreementList
                      {...props}
                      typeOfStudent={0}
                      userHeiID={user.heiID}
                    />
                  ),
                  t('incomingStudents.tab')
                ),
                t('incomingStudents.title'),
                t('incomingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/certificates/arrival-departures"
          element={
            <Navigate
              to="/certificates/arrival-departures/incoming"
              replace
            />
          }
        />
        <Route
          path="/certificates/arrival-departures/incoming"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <CertificatesArrivalDeparturesList
                      {...props}
                      userHeiID={user.heiID}
                    />
                  ),
                  t('certificatesArrivalDepartures.tab')
                ),
                t('certificatesArrivalDepartures.title'),
                t('certificatesArrivalDepartures.subTitleIncoming')
              )
            )
          )}
        />
        <Route
          path="/certificates/arrival-departures/outgoing"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => <CertificatesOutgoingView {...props} />,
                  t('certificatesArrivalDepartures.tab')
                ),
                t('certificatesArrivalDepartures.title'),
                t('certificatesArrivalDepartures.subTitleOutgoing')
              )
            )
          )}
        />
        <Route
          path="/short-term/outgoing"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <ShortTermMobilitiesList
                      {...props}
                      typeOfStudent={1}
                      userHeiID={user.heiID}
                    />
                  ),
                  t('shortTerm.tabOutgoing')
                ),
                t('shortTerm.title'),
                t('outgoingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/short-term/incoming"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <ShortTermMobilitiesList
                      {...props}
                      typeOfStudent={0}
                      userHeiID={user.heiID}
                    />
                  ),
                  t('shortTerm.tabIncoming')
                ),
                t('shortTerm.title'),
                t('incomingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/ola/outgoing/:id"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <SingleViewLA {...props} typeOfStudent={"outgoing"} />
                  ),
                  t('outgoingStudents.tabSingleView')
                ),
                t('outgoingStudents.title'),
                t('outgoingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/ola/incoming/:id"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <SingleViewLA {...props} typeOfStudent={"incoming"} />
                  ),
                  t('incomingStudents.tabSingleView')
                ),
                t('outgoingStudents.title'),
                t('incomingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/short-term/outgoing/:id"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <SingleViewShortTermMobility
                      {...props}
                      typeOfStudent={"outgoing"}
                    />
                  ),
                  t('shortTermMobilities.tabOutgoing')
                ),
                t('shortTermMobilities.title'),
                t('outgoingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/short-term/incoming/:id"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <SingleViewShortTermMobility
                      {...props}
                      typeOfStudent={"incoming"}
                    />
                  ),
                  t('shortTermMobilities.tabIncoming')
                ),
                t('shortTermMobilities.title'),
                t('incomingStudents.subTitle')
              )
            )
          )}
        />
        <Route path="/ola/outgoing/export/:uuid" element={<ExportOlaPdf />} />
        <Route path="/ola/incoming/export/:uuid" element={<ExportOlaPdf />} />
        <Route
          path="/short-term/outgoing/export/:uuid"
          element={<ExportShortTermPdf />}
        />
        <Route
          path="/short-term/incoming/export/:uuid"
          element={<ExportShortTermPdf />}
        />
        <Route
          path="/ola/outgoing/export/:uuid/revision/:vid"
          element={<ExportOlaPdf />}
        />
        <Route
          path="/ola/incoming/export/:uuid/revision/:vid"
          element={<ExportOlaPdf />}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["uploadingStudents"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_ola),
            ]}
          />
        }
      >
        <Route
          path="/ola/upload"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  () => <Upload />,
                  t('learningAgreements.upload.tab')
                ),
                t('learningAgreements.upload.titleMain'),
                t('learningAgreements.upload.subTitle')
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["managingStudents"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_ola),
            ]}
          />
        }
      >
        <Route
          path="/courses/list"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <CoursesEvaluationsList />
                  ),
                  t('courses.title')
                ),
                t('courses.title'),
                t('courses.subtitleList')
              )
            )
          )}
        />
        <Route
          path="/courses/single-view/:id"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <CoursesEvaluationsSingleView />
                  ),
                  t('courses.title')
                ),
                t('courses.title'),
                t('courses.subtitleSingleView')
              )
            )
          )}
        />
        <Route path="/courses/export/:id" element={<ExportPdfCourse />} />
        
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["nominations"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_nomination),
            ]}
          />
        }
      >
        <Route
          path="/applications-for-erasmus+/outgoing-students/:id"
          element={Sidebar(
            withTitle(
              SingleViewOutgoingNominations,
              "View single Outgoing Nomination"
            )
          )}
        />
        <Route
          path="/applications-for-erasmus+/incoming-students/:id"
          element={Sidebar(
            withTitle(
              SingleViewIncomingNominations,
              "View single Incoming Nomination"
            )
          )}
        />
        <Route
          path="/applications-for-erasmus+/outgoing-students/export/:id/:uuid"
          element={<ExportPdf />}
        />
        <Route
          path="/applications-for-erasmus+/incoming-students/export/:id/:uuid"
          element={<ExportPdf />}
        />
        {/* <Route
          path="/applications-for-erasmus+/requirements"
          element={Sidebar(RequirementsNominations)}
        /> */}

        <Route
          path="/applications-for-erasmus+/incoming-students"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <NominationsList
                      {...props}
                      userHeiID={user.heiID}
                      userEC={user.erasmusCode}
                    />
                  ),
                  t('applications.tab.in')
                ),
                t('applications.title'),
                t('incomingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/applications-for-erasmus+/outgoing-students"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <NominationsList
                      {...props}
                      userHeiID={user.heiID}
                      userEC={user.erasmusCode}
                    />
                  ),
                  t('applications.tab.out')
                ),
                t('applications.title'),
                t('outgoingStudents.subTitle')
              )
            )
          )}
        />
        <Route
          path="/applications-for-erasmus+/upload"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  () => <ImportNomination />,
                  t('learningAgreements.upload.tab')
                ),
                t('applications.title'),
                t('learningAgreements.upload.subTitle')
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["managingIIAs"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_iia),
            ]}
          />
        }
      >
        <Route path="/iias/single-view/:id" element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <SingleViewIIAs {...props} />, t('iiaSingleView.IIASingleView')),
                t('iiasEdit.title'),
                t('iiaSingleView.singleView')
              )
            )
          )} />
        <Route
          path="/iias/edit/:id"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <CreateIIA {...props} />, t('iiasEdit.tab')),
                t('iiasEdit.title'),
                t('iiasEdit.subTitle')
              )
            )
          )}
        />
        <Route path="/iias/export/:id" element={<ViewPdf />} />
        <Route
          path="/iias/export"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <ExportIIAs {...props} />, t('iiaExport.subTitle')),
                t('iiasEdit.title'),
                t('iiaExport.subTitle')
              )
            )
          )}
        />
        <Route
          path="/iias/iia-list"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <InterInstitutionalAgreementList userHeiID={user.heiID} />
                  ),
                  t('iiaList.title')
                ),
                t('iiaList.title'),
                t('iiaList.subTitle')
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["creatingIIA"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_iia),
            ]}
          />
        }
      >
        <Route
          path="/iias/create-iia"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <CreateIIA {...props} />, t('iiasEdit.createTab')),
                t('iiasEdit.title'),
                t('iiasEdit.createNewSubTitle')
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["importIIA"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_iia),
            ]}
          />
        }
      >
        <Route
          path="/iias/import"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <ImportIIA {...props} />, t('iiaImport.subTitle')),
                t('iiasEdit.title'),
                t('iiaImport.subTitle')
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["defaultIIAData"]}
            extraConditions={[
              loadingEWPSettings ||
                networkStatusEWPSettings === NetworkStatus.loading ||
                networkStatusEWPSettings === NetworkStatus.refetch ||
                (dataEWPSettings?.ewpConnections?.length > 0 &&
                  dataEWPSettings?.ewpConnections[0]?.api_static),
            ]}
          />
        }
      >
        <Route
          path="/iias/edit-factsheet"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => <EditFactsheet userHeiID={user.heiID} />,
                  t('iiaList.title')
                ),
                t('iiaList.title'),
                t('editFactsheet.subTitle')
              )
            )
          )}
        />
      </Route>
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["erasmus-app"]}
          />
        }
      >
        <Route
          path="/erasmus+-app/useful-info"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <UsefulInfo {...props} />, "Useful Info"),
                "Erasmus+ App",
                "Useful Info"
              )
            )
          )}
        />
        <Route
          path="/erasmus+-app/events"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <Event {...props} />, "Event"),
                "Erasmus+ App",
                "Events"
              )
            )
          )}
        />
        <Route
          path="/erasmus+-app/deals"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle((props) => <Deals {...props} />, "Deals"),
                "Erasmus+ App",
                "Deals"
              )
            )
          )}
        />
      </Route>

      {/* Route for Course Catalogue */}
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["manageCC"]}
          />
        }
      >
        <Route
          path="/cc"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <CourseCatalogue />
                  ),
                  t('courseCatalogue.courseCatalogue')
                ),
                t('courseCatalogue.courseCatalogue')
              )
            )
          )}
        />
        <Route
          path="/cc/programmes"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <Programmes />
                  ),
                  t('courseCatalogue.programmes')
                ),
                t('courseCatalogue.programmes'),
                t('courseCatalogue.listOfProgrammes')
              )
            )
          )}
        />

        <Route
          path="/cc/programmes/:programmeID"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <SingleViewProgramme />
                  ),
                  t('courseCatalogue.programme')
                ),
                t('courseCatalogue.programme')
              )
            )
          )}
        />

        <Route
          path="/cc/programmes/upload"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <UploadProgramme />
                  ),
                  t('courseCatalogue.programme')
                ),
                t('courseCatalogue.programme'),
                t('courseCatalogue.uploadCSV')
              )
            )
          )}
        />

        <Route
          path="/cc/courses"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <Courses />
                  ),
                  t('courseCatalogue.courses')
                ),
                t('courseCatalogue.courses'),
                t('courseCatalogue.listOfCourses')
              )
            )
          )}
        />

        <Route
          path="/cc/courses/:courseID"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <SingleViewCourse />
                  ),
                  t('courseCatalogue.course')
                ),
                t('courseCatalogue.course')
              )
            )
          )}
        />

        <Route
          path="/cc/courses/upload"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <UploadCourse />
                  ),
                  t('courseCatalogue.course')
                ),
                t('courseCatalogue.course'),
                t('courseCatalogue.uploadCSV')
              )
            )
          )}
        />

      </Route>
      {/* Route for Course Catalogue */}

      {/* Traineeships Routes */}
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
            allowedPermissions={["viewingStudents", "managingStudents"]}
          />
        }
      >
        <Route
          path="/traineeships/outgoing"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <OutgoingTrainseeshipList
                      {...props}
                      userHeiID={user.heiID}
                    />
                  ),
                 t('traineeships.title')
                ),
                t('traineeships.subtitle'),
                t('traineeships.title')
              )
            )
          )}
        />

        <Route
          path="/traineeships/outgoing/:traineeshipID"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(
                  (props) => (
                    <SingleViewTraineeship
                      {...props}
                    />
                  ),
                  t('traineeships.title')
                ),
                t('traineeships.subtitle'),
                t('traineeships.title')
              )
            )
          )}
        />
      </Route>
      
      <Route
        element={
          <RequireAuth
            allowedUserTypes={[ACCOUNT_TYPES.hei]}
          />
        }
      >
        <Route
          path="/factsheets"
          element={
            Sidebar(
              withFooter(
                withNavBar(
                  withTitle(
                    (props) => (
                      <FactsheetBrowser />
                    ),
                    t('factsheetBrowser.title')
                  ),
                  t('factsheetBrowser.title'),
                  t('factsheetBrowser.subTitle')
                )
              )
            )
            
          }
        />

        <Route
          path="/factsheets/single-view/:heiID"
          element={
            Sidebar(
              withFooter(
                withNavBar(
                  withTitle(
                    (props) => (
                      <PartnerFactSheetSingleView {...props} />
                    ),
                    t('factsheetBrowser.singleView.title')
                  ),
                  t('factsheetBrowser.singleView.title'),
                  t('factsheetBrowser.singleView.subTitle')
                )
              )
            )
            
          }
        />
      </Route>

      <Route
          path="/unauthorized"
          element={Sidebar(
            withFooter(
              withNavBar(
                withTitle(() => <UnauthorizedPage/>, "Unauthorized"),
                "",
                ""
              )
            )
          )}
        />
      {process.env.NODE_ENV === "development" && (
        <Route path="components" element={<ReusableComponents />} />
      )}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
